import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import businessCategoryConfig from '../../business-category-config';
import defaultSearchesList from './default-searches';
import config from '../../config';
import CategoryDropdownSection from './CategoryDropdownSection';

import css from './MainFiltersForm.css';

const TOTAL_NUMBER_OF_SUGGESTIONS = 5;

const phraseIsWord = (itemText, keywordValue = '') => {
  if (!itemText) return false;
  const text = itemText.toLowerCase();
  const query = keywordValue.toLowerCase().trim();
  if (!query) return false;
  const isExact = query === text;
  const wordBegin = text.includes(`${query} `) && text.indexOf(query) === 0;
  const wordMiddle = text.includes(` ${query} `);
  const wordEnd = text.includes(` ${query}`) && text.indexOf(query) + query.length === text.length;

  return wordBegin || wordMiddle || wordEnd || isExact;
};

const getLabelObj = (item, categories) => {
  if (!item || !item.id || !item.attributes) return null;
  const {
    attributes: { parentId },
    id: { uuid },
  } = item;
  const categoryLabel =
    !parentId && categories ? categories.find(option => option.key === uuid) : null;
  const subcategoryLabel = parentId ? businessCategoryConfig.find(({ key }) => key === uuid) : null;
  return parentId ? subcategoryLabel : categoryLabel;
};

const sortSuggestions = (items, query, intl, categories) => {
  const getTranslationString = item => {
    const labelObj = getLabelObj(item, categories);
    return labelObj?.translation ? intl.formatMessage({ id: labelObj.translation }) : '';
  };

  const filteredFittedCategory = items.filter(item => {
    const label = getTranslationString(item);
    return phraseIsWord(label, query) && item.attributes?.keyword?.exact;
  });

  const filteredOtherCategory = items.filter(item => {
    const label = getTranslationString(item);
    return !phraseIsWord(label, query) && item.attributes?.keyword?.exact;
  });

  const filteredFittedText = items.filter(item => {
    const { attributes: { keyword: { exact, text } = {} } = {} } = item;
    return phraseIsWord(text, query) && !exact;
  });

  const filteredOtherText = items.filter(item => {
    const { attributes: { keyword: { exact, text } = {} } = {} } = item;
    return !phraseIsWord(text, query) && !exact;
  });

  return [
    ...filteredFittedCategory,
    ...filteredOtherCategory,
    ...filteredFittedText,
    ...filteredOtherText,
  ];
};

const CategorySelector = ({
  searchQuery = '',
  suggestions = [],
  categories = [],
  placeholder = '',
  onInputChange,
  onClear,
  onSelect,
  setActiveSection,
  recentWhat = [],
  handleClearRecentWhat,
}) => {
  const intl = useIntl();
  const currentCountryId = config.custom.countryId || 'pl';
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const list = useMemo(() => {
    const query = searchQuery?.toLowerCase().trim();
    if (!query) {
      const remainingSlots = TOTAL_NUMBER_OF_SUGGESTIONS - recentWhat.length;
      if (remainingSlots <= 0) return [];

      return defaultSearchesList
        .filter(item => item.countryId === currentCountryId)
        .sort((a, b) => Number(b.exact) - Number(a.exact))
        .slice(0, remainingSlots);
    }

    const categoryMatches = [];
    const keywordMatches = [];
    const seenCategories = new Set();
    const seenKeywords = new Set();

    suggestions.forEach(suggestion => {
      const { attributes, id } = suggestion;
      const keywordsList = attributes?.keywords || [];
      const labelObj = getLabelObj(suggestion, categories);
      const categoryLabelText = labelObj?.translation
        ? intl.formatMessage({ id: labelObj.translation })
        : '';

      keywordsList.forEach(kw => {
        const textLower = kw.text?.toLowerCase() || '';
        if (!textLower.includes(query)) return;
        const virtualSuggestion = { id: id, attributes: { ...attributes, keyword: kw } };

        if (kw.exact) {
          if (seenCategories.has(categoryLabelText.toLowerCase().trim())) return;
          seenCategories.add(categoryLabelText.toLowerCase().trim());
          categoryMatches.push(virtualSuggestion);
        } else {
          const keywordKey = `${kw.text
            ?.toLowerCase()
            .trim()}_${categoryLabelText.toLowerCase().trim()}`;
          if (kw.text?.toLowerCase().trim() === categoryLabelText.toLowerCase().trim()) return;
          if (seenKeywords.has(keywordKey)) return;
          seenKeywords.add(keywordKey);
          keywordMatches.push(virtualSuggestion);
        }
      });
    });

    const sortedCategories = sortSuggestions(categoryMatches, query, intl, categories);
    const sortedKeywords = sortSuggestions(keywordMatches, query, intl, categories);

    return [...sortedCategories, ...sortedKeywords].slice(0, TOTAL_NUMBER_OF_SUGGESTIONS);
  }, [searchQuery, suggestions, categories, intl, currentCountryId, recentWhat.length]);

  const combinedList = useMemo(() => {
    return !searchQuery ? [...recentWhat, ...list] : list;
  }, [searchQuery, recentWhat, list]);

  const getDisplayedCategoryData = useCallback(
    item => {
      const { attributes: { keyword, parentId } = {}, id: { uuid } = {} } = item;
      const isSuggestion = !!keyword;

      const labelObj = isSuggestion
        ? getLabelObj(item, categories)
        : businessCategoryConfig.find(o => o.key === item.subcategory) ||
          categories.find(o => o.key === item.category);

      const labelText = labelObj?.translation
        ? intl.formatMessage({ id: labelObj.translation })
        : '';
      const displayKeyword = isSuggestion
        ? keyword.exact
          ? labelText
          : keyword.text
        : item.exact && labelObj
        ? labelText
        : item.keyword;

      const categoryId = isSuggestion ? (!parentId ? uuid : parentId) : item.category;
      const subcategoryId = isSuggestion ? (parentId ? uuid : '') : item.subcategory;
      const exact = isSuggestion ? keyword.exact : item.exact;

      return {
        displayKeyword,
        categoryId,
        subcategoryId,
        exact,
        labelText,
        labelObj,
        isSuggestion,
        keyword,
      };
    },
    [categories, intl]
  );

  const handleSelectItem = useCallback(
    item => {
      if (!item) return;
      const data = getDisplayedCategoryData(item);
      onSelect({
        keyword: data.displayKeyword,
        category: data.categoryId,
        subcategory: data.subcategoryId,
        exact: data.exact,
      });
    },
    [getDisplayedCategoryData, onSelect]
  );

  const handleKeyDown = e => {
    if (!combinedList || combinedList.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => (prev < combinedList.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => (prev > 0 ? prev - 1 : combinedList.length - 1));
    } else if (e.key === 'Escape') {
      setFocusedIndex(-1);
      inputRef.current && inputRef.current.blur();
      setActiveSection(null);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const targetIdx = focusedIndex >= 0 ? focusedIndex : 0;
      if (focusedIndex >= 0) {
        !!combinedList[focusedIndex] && handleSelectItem(combinedList[targetIdx]);
      } else {
        inputRef.current && inputRef.current.blur();
        setActiveSection(null);
      }
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    setFocusedIndex(-1);
  }, [searchQuery]);

  useEffect(() => {
    if (focusedIndex >= 0 && scrollContainerRef.current) {
      const itemElement = scrollContainerRef.current.children[focusedIndex];
      if (itemElement) {
        itemElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusedIndex]);

  return (
    <>
      <div className={css.inputWrapper}>
        <input
          ref={inputRef}
          id="category-input"
          className={css.invisibleInput}
          value={searchQuery}
          onChange={e => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
        />
        {searchQuery && (
          <button type="button" className={css.clearBtn} onClick={onClear}>
            <span className={css.clearIcon}>×</span>
          </button>
        )}
      </div>
      <div
        className={classNames(css.dropdownBase, css.categoryDropdown)}
        onClick={e => e.stopPropagation()}
      >
        <div className={css.scrollContainer}>
          {searchQuery.length > 0 && list.length === 0 && (
            <div className={css.sectionGroup}>
              <div className={css.dropdownHeaderContainer}>
                <p className={css.dropdownHeader}>
                  {<FormattedMessage id="MarketplaceSearchBar.searchResults" />}
                </p>
              </div>
              <div className={css.noResults}>
                <FormattedMessage id="MarketplaceSearchBar.noResults" />
              </div>
            </div>
          )}
          {!searchQuery && recentWhat.length > 0 && (
            <CategoryDropdownSection
              title={<FormattedMessage id="MainFilters.latestSearch" />}
              items={recentWhat}
              focusedIndex={focusedIndex}
              startIndex={0}
              getDisplayedCategoryData={getDisplayedCategoryData}
              onSelectItem={handleSelectItem}
              keyPrefix="recent"
              clearButton={
                <button
                  type="button"
                  className={css.clearRecentBtn}
                  onClick={handleClearRecentWhat}
                >
                  <span className={css.clearIcon}>×</span>
                </button>
              }
            />
          )}
          {list.length > 0 && (
            <CategoryDropdownSection
              title={<FormattedMessage id="MainFilters.suggestions" />}
              items={list}
              focusedIndex={focusedIndex}
              startIndex={!searchQuery ? recentWhat.length : 0}
              getDisplayedCategoryData={getDisplayedCategoryData}
              onSelectItem={handleSelectItem}
              keyPrefix={searchQuery ? 'sug' : 'def'}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default CategorySelector;
